import type { ChapterNotes } from '@curriculum/notes';

/**
 * Renders exam-complete chapter notes (overview, key terms, point-wise
 * concepts, formulas/formats, important Q&A with marks, exam tips, quick
 * revision). Khadi & Indigo theme. Data is independently fact-checked.
 */
export function ChapterNotesView({ notes }: { notes: ChapterNotes }) {
  return (
    <div className="space-y-10">
      <div>
        <a
          href={`/downloads/${notes.chapterId}.pdf`}
          download
          className="aces-btn-primary px-5 py-2.5"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" className="w-4 h-4" aria-hidden="true">
            <path d="M8 2v8m0 0L5 7m3 3 3-3M3 13h10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Download notes (PDF)
        </a>
      </div>

      {notes.overview && <p className="text-ink-700 text-[1.05rem] leading-[1.75] max-w-read">{notes.overview}</p>}

      {notes.keyTerms.length > 0 && (
        <div>
          <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-4">Key terms &amp; definitions</h3>
          <dl className="grid gap-2.5 sm:grid-cols-2">
            {notes.keyTerms.map((t) => (
              <div key={t.term} className="rounded-xl border border-ink-200/80 bg-cream px-4 py-3 shadow-soft">
                <dt className="font-semibold text-ink-900 text-sm">{t.term}</dt>
                <dd className="text-ink-700 text-sm mt-0.5 leading-relaxed">{t.definition}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {notes.concepts.length > 0 && (
        <div className="space-y-8">
          {notes.concepts.map((c) => (
            <div key={c.heading}>
              <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-3">{c.heading}</h3>
              <ul className="space-y-2">
                {c.points.map((p, i) => (
                  <li key={i} className="flex gap-2.5 text-[15px] text-ink-700 leading-[1.7]">
                    <span className="text-accent-500 mt-0.5 shrink-0" aria-hidden="true">•</span><span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {notes.formulasFormats.length > 0 && (
        <div className="rounded-card-lg border border-ink-200/80 bg-gradient-to-b from-ink-50 to-ink-100/50 p-5 sm:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
          <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-4">Formulas &amp; formats</h3>
          <ul className="space-y-2">
            {notes.formulasFormats.map((f, i) => (
              <li key={i} className="overflow-x-auto rounded-lg border border-ink-200/70 bg-cream px-3.5 py-2.5 text-sm text-ink-800 leading-relaxed font-mono whitespace-pre-wrap">{f}</li>
            ))}
          </ul>
        </div>
      )}

      {notes.importantQuestions.length > 0 && (
        <div>
          <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-4">Important questions &amp; model answers</h3>
          <div className="space-y-4">
            {notes.importantQuestions.map((q, i) => (
              <div key={i} className="rounded-card border border-ink-200/80 bg-cream p-4 sm:p-5 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-ink-900 text-[15px] leading-snug">{q.q}</p>
                  <span className="shrink-0 text-[11px] font-semibold text-accent-700 bg-accent-50 border border-accent-200/70 rounded-full px-2.5 py-0.5">
                    {q.marks} mark{q.marks > 1 ? 's' : ''}
                  </span>
                </div>
                <ul className="mt-3 space-y-1.5 border-t border-ink-200/60 pt-3">
                  {q.answer.map((a, j) => (
                    <li key={j} className="flex gap-2 text-sm text-ink-700 leading-relaxed">
                      <span className="text-econ-500 mt-1 shrink-0" aria-hidden="true">→</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {notes.examTips.length > 0 && (
        <div className="rounded-card bg-gradient-to-r from-accent-50 to-cream px-5 py-5 border border-accent-100 border-l-4 border-l-accent-500 shadow-soft">
          <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-3">Exam tips</h3>
          <ul className="space-y-1.5">
            {notes.examTips.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm text-ink-700 leading-relaxed">
                <span className="text-accent-600 mt-0.5 shrink-0" aria-hidden="true">★</span><span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {notes.revision.length > 0 && (
        <div>
          <h3 className="font-serif text-xl text-ink-900 tracking-tight mb-3">Quick revision</h3>
          <ul className="space-y-1.5">
            {notes.revision.map((r, i) => (
              <li key={i} className="flex gap-2 text-sm text-ink-700 leading-relaxed">
                <span className="text-ink-400 mt-1 shrink-0" aria-hidden="true">•</span><span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
